import { useState, type CSSProperties, type FormEvent } from "react";
import type { AppController, AppSnapshot, ExecuteAction, GoalKind, GoalSettings } from "../types";

interface OnboardingViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function OnboardingView({ snapshot, controller, execute }: OnboardingViewProps) {
  const [step, setStep] = useState(1);
  const [startingWeight, setStartingWeight] = useState(snapshot.weight.latest ? String(snapshot.weight.latest) : "");
  const [baselineCalories, setBaselineCalories] = useState(snapshot.week.baselineDailyCalories > 0 ? String(snapshot.week.baselineDailyCalories) : "");
  const [goalSettings, setGoalSettings] = useState(snapshot.goals);
  const [saving, setSaving] = useState(false);

  const weightValid = Number(startingWeight) >= 20 && Number(startingWeight) <= 400;
  const goalValid = goalSettings.kind === "maintain"
    ? goalSettings.maintenanceGoalWeightKg >= 20 && goalSettings.maintenanceTolerancePercent > 0
    : goalSettings.weeklyRatePercent > 0;
  const proteinCalories = Number(startingWeight) * proteinFactor(goalSettings.kind) * 4;
  const dailyFloor = calculateFirstWeekFloor(
    proteinCalories,
    goalSettings.fatShareOfRemainder,
    snapshot.checkIn.dailyFloorPolicy,
  );
  const caloriesValid = Number.isFinite(Number(baselineCalories)) && Number(baselineCalories) >= dailyFloor && Number(baselineCalories) <= 50_000;
  const meetsDailyFloor = Number(baselineCalories) >= dailyFloor;

  function setGoal(kind: GoalKind) {
    setGoalSettings((current) => ({ ...current, kind }));
  }

  function setCarbohydratePercent(value: number) {
    const carbohydrateShareOfRemainder = Math.max(0, Math.min(0.95, value / 100));
    setGoalSettings((current) => ({
      ...current,
      carbohydrateShareOfRemainder,
      fatShareOfRemainder: 1 - carbohydrateShareOfRemainder,
    }));
  }

  async function finish(event: FormEvent) {
    event.preventDefault();
    if (!weightValid || !caloriesValid || !goalValid || !meetsDailyFloor) return;
    setSaving(true);
    await execute(
      () => controller.completeOnboarding({
        baselineCalories: Math.round(Number(baselineCalories)),
        startingWeightKg: Number(startingWeight),
        goalSettings,
      }),
      { errorMessage: "Your first week could not be created. Check the details and try again." },
    );
    setSaving(false);
  }

  return (
    <section className="onboarding" aria-labelledby="onboarding-heading">
      <header className="onboarding__heading">
        <p className="eyebrow">Set up your first week</p>
        <h1 id="onboarding-heading">A flexible plan that keeps protein steady.</h1>
        <p>Start with what you know today. You can review your goals and calorie baseline at every weekly check-in.</p>
      </header>

      <ol className="stepper onboarding__stepper" aria-label="Setup progress">
        {["Starting point", "Goal", "Macros"].map((label, index) => {
          const number = index + 1;
          return <li key={label} className={number === step ? "stepper__item stepper__item--current" : number < step ? "stepper__item stepper__item--done" : "stepper__item"}><span>{number < step ? "OK" : number}</span><small>{label}</small></li>;
        })}
      </ol>

      {step === 1 && (
        <article className="card onboarding__card">
          <div className="card__heading"><div><h2>Your starting point</h2><p>This weight sets your first daily protein target. Future weeks use your five-day trend.</p></div></div>
          <div className="stack">
            <label className="field">
              <span>Current weight</span>
              <span className="input-with-suffix input-with-suffix--large"><input type="number" inputMode="decimal" min="20" max="400" step="0.1" required placeholder="Enter weight" value={startingWeight} onChange={(event) => setStartingWeight(event.target.value)} /><i>kg</i></span>
              <small>For consistency, weigh in the morning before food or drink and after using the toilet when possible.</small>
            </label>
            <label className="field">
              <span>Current daily calorie target</span>
              <span className="input-with-suffix input-with-suffix--large"><input type="number" inputMode="numeric" min={dailyFloor} max="50000" step="1" required placeholder="Enter calories" value={baselineCalories} onChange={(event) => setBaselineCalories(event.target.value)} /><i>kcal</i></span>
              <small>Your calculated first-week floor is {dailyFloor.toLocaleString()} kcal. Use a target you already follow or have chosen with an appropriate health professional; the app does not estimate one from demographic data.</small>
            </label>
            <button className="button button--primary button--full" type="button" disabled={!weightValid || !caloriesValid} onClick={() => setStep(2)}>Choose your goal</button>
          </div>
        </article>
      )}

      {step === 2 && (
        <article className="card onboarding__card">
          <div className="card__heading"><div><h2>What are you working toward?</h2><p>Your goal guides weekly recommendations; adjustment safeguards limit sudden changes.</p></div></div>
          <div className="stack">
            <fieldset className="fieldset">
              <legend>Long-term goal</legend>
              <div className="segmented-control segmented-control--three">
                {(["lose", "maintain", "gain"] as const).map((kind) => (
                  <label key={kind} className={goalSettings.kind === kind ? "segmented-control__item segmented-control__item--selected" : "segmented-control__item"}>
                    <input type="radio" name="onboarding-goal" checked={goalSettings.kind === kind} onChange={() => setGoal(kind)} />
                    <span>{kind === "lose" ? "Lose" : kind === "gain" ? "Gain" : "Maintain"}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {goalSettings.kind === "maintain" ? (
              <div className="form-grid">
                <label className="field"><span>Goal weight</span><span className="input-with-suffix"><input type="number" min="20" max="400" step="0.1" value={goalSettings.maintenanceGoalWeightKg || ""} onChange={(event) => setGoalSettings({ ...goalSettings, maintenanceGoalWeightKg: Number(event.target.value) })} /><i>kg</i></span></label>
                <label className="field"><span>Tolerance around goal</span><span className="input-with-suffix"><input type="number" min="0.1" max="20" step="0.1" value={goalSettings.maintenanceTolerancePercent || ""} onChange={(event) => setGoalSettings({ ...goalSettings, maintenanceTolerancePercent: Number(event.target.value) })} /><i>+/- %</i></span></label>
              </div>
            ) : (
              <label className="field"><span>Target change per week</span><span className="input-with-suffix"><input type="number" min="0.05" max="5" step="0.05" value={goalSettings.weeklyRatePercent || ""} onChange={(event) => setGoalSettings({ ...goalSettings, weeklyRatePercent: Number(event.target.value) })} /><i>% body weight</i></span></label>
            )}

            <label className="field"><span>Weekly check-in day</span><select value={goalSettings.checkInDay} onChange={(event) => setGoalSettings({ ...goalSettings, checkInDay: Number(event.target.value) })}>{DAYS.map((day, index) => <option value={index} key={day}>{day}</option>)}</select></label>

            <div className="button-row button-row--split">
              <button className="button button--ghost" type="button" onClick={() => setStep(1)}>Back</button>
              <button className="button button--primary" type="button" disabled={!goalValid} onClick={() => setStep(3)}>Set macro split</button>
            </div>
          </div>
        </article>
      )}

      {step === 3 && (
        <form className="stack" onSubmit={finish}>
          <article className="card onboarding__card">
            <div className="card__heading"><div><h2>Split the calories after protein</h2><p>Your daily protein is calculated from starting weight and held steady all week.</p></div></div>
            <div className="onboarding-protein">
              <span>First-week protein</span>
              <strong>{Math.round(Number(startingWeight) * proteinFactor(goalSettings.kind))} g every day</strong>
              <small>{proteinFactor(goalSettings.kind).toFixed(1)} g/kg for a {goalSettings.kind === "maintain" ? "maintenance" : `${goalSettings.kind} phase`}</small>
            </div>
            <MacroPreview baselineCalories={Number(baselineCalories)} weightKg={Number(startingWeight)} settings={goalSettings} />
            {!meetsDailyFloor && (
              <div className="notice notice--warning" role="alert"><div><strong>The calorie baseline is below the {dailyFloor.toLocaleString()} kcal daily floor</strong><p>Go back and raise the baseline. The floor allows for fixed protein plus minimum remaining energy and fat.</p></div></div>
            )}
            <label className="range-field">
              <span><strong>Remaining calorie split</strong><output>{Math.round(goalSettings.carbohydrateShareOfRemainder * 100)}% carbs / {Math.round(goalSettings.fatShareOfRemainder * 100)}% fat</output></span>
              <input type="range" min="0" max="95" step="1" value={Math.round(goalSettings.carbohydrateShareOfRemainder * 100)} onChange={(event) => setCarbohydratePercent(Number(event.target.value))} />
              <small><span>More fat</span><span>More carbs</span></small>
            </label>
            <p className="field-hint">Fibre is included within carbohydrate. Calories are calculated as protein 4 kcal/g, carbohydrate 4 kcal/g and fat 9 kcal/g.</p>
          </article>

          <article className="card onboarding__card">
            <div className="card__heading"><h2>Your first week</h2></div>
            <dl className="definition-list">
              <div><dt>Daily baseline</dt><dd>{Number(baselineCalories).toLocaleString()} kcal</dd></div>
              <div><dt>Seven-day budget</dt><dd>{(Number(baselineCalories) * 7).toLocaleString()} kcal</dd></div>
              <div><dt>Goal</dt><dd>{goalLabel(goalSettings)}</dd></div>
              <div><dt>Check-in</dt><dd>{DAYS[goalSettings.checkInDay]}</dd></div>
            </dl>
            <p className="supporting-text">Your seven days start equal. Once setup is complete, use the Week screen to move calories between them.</p>
          </article>

          <div className="button-row button-row--split">
            <button className="button button--ghost" type="button" onClick={() => setStep(2)}>Back</button>
            <button className="button button--primary" type="submit" disabled={saving || !meetsDailyFloor}>{saving ? "Creating your week..." : "Create my first week"}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function MacroPreview({ baselineCalories, weightKg, settings }: { baselineCalories: number; weightKg: number; settings: GoalSettings }) {
  const proteinCalories = weightKg * proteinFactor(settings.kind) * 4;
  const proteinPercent = Math.max(0, Math.min(100, proteinCalories / baselineCalories * 100));
  const remainder = Math.max(0, 100 - proteinPercent);
  const carbPercent = remainder * settings.carbohydrateShareOfRemainder;
  const fatPercent = remainder * settings.fatShareOfRemainder;
  const style = { "--protein-stop": `${proteinPercent}%`, "--carb-stop": `${proteinPercent + carbPercent}%` } as CSSProperties;
  return (
    <div className="macro-preference__visual onboarding-macro-preview">
      <div className="macro-pie" style={style} role="img" aria-label={`${Math.round(proteinPercent)} percent protein, ${Math.round(carbPercent)} percent carbohydrate and ${Math.round(fatPercent)} percent fat`}><span>{baselineCalories}<small>kcal/day</small></span></div>
      <ul className="macro-key">
        <li><i className="macro-key__protein" /><span>Protein</span><strong>{Math.round(proteinPercent)}%</strong></li>
        <li><i className="macro-key__carbs" /><span>Carbs</span><strong>{Math.round(carbPercent)}%</strong></li>
        <li><i className="macro-key__fat" /><span>Fat</span><strong>{Math.round(fatPercent)}%</strong></li>
      </ul>
    </div>
  );
}

function proteinFactor(kind: GoalKind) {
  return kind === "gain" ? 1.7 : kind === "lose" ? 2 : 1.8;
}

function calculateFirstWeekFloor(
  proteinCalories: number,
  fatShareOfRemainder: number,
  policy: AppSnapshot["checkIn"]["dailyFloorPolicy"],
) {
  const fatShare = Math.max(Number.EPSILON, fatShareOfRemainder);
  return Math.ceil(Math.max(
    policy.absoluteMinimumCalories,
    proteinCalories + policy.minimumNonProteinCalories,
    proteinCalories + (policy.minimumFatGrams * 9) / fatShare,
  ));
}

function goalLabel(settings: GoalSettings) {
  if (settings.kind === "maintain") return `Maintain ${settings.maintenanceGoalWeightKg} kg within +/-${settings.maintenanceTolerancePercent}%`;
  return `${settings.kind === "gain" ? "Gain" : "Lose"} ${settings.weeklyRatePercent}% per week`;
}
